import { useEffect, useState } from "react";
import { Activity, AlertTriangle, CheckCircle2, FolderOpen, HardDrive, Loader2, Pause, Play, RefreshCw, ShieldAlert, UploadCloud, FileWarning } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/auth";
import DriveHealthDetailsDialog from "@/pages/settings/DriveHealthDetailsDialog";

/**
 * Iter-160 — Drive Health block.
 * Surfaces operational metrics for the local-files purge cron + Drive integration.
 * - Pending purge backlog (POs + invoices > 15 days, not yet purged)
 * - Last cron run summary (verified / unverified / rattrapage failures)
 * - Clients without Drive folder (link to wizard)
 * - CEO Drive token presence (otherwise nothing can be verified)
 * - "Run purge now" button (calls Iter-159 manual trigger)
 */
export default function DriveHealthBlock() {
  const { user } = useAuth();
  const isCEO = user?.role === "CEO";
  const [health, setHealth] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [togglingPurge, setTogglingPurge] = useState(false);

  // Iter-181 — invoices missing in the default Drive folder
  const [invCheck, setInvCheck] = useState(null);   // null | result
  const [invLoading, setInvLoading] = useState(false);
  const [invUploading, setInvUploading] = useState(false);

  // Iter-182 — CRA exports missing in the mission/client CRA folder
  const [craCheck, setCraCheck] = useState(null);
  const [craLoading, setCraLoading] = useState(false);
  const [craUploading, setCraUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [h, r] = await Promise.all([
        api.get("/admin/drive-health"),
        api.get("/admin/drive-health/purge-runs?limit=5"),
      ]);
      setHealth(h.data);
      setRuns(r.data?.runs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Drive health");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Iter-193 — listen for wizard completion so the "Clients without Drive" alert
  // count is refreshed live (was a stale `1` after the wizard had cleared the
  // backlog in production).
  useEffect(() => {
    const onChanged = () => { load(); };
    window.addEventListener("drive-health-changed", onChanged);
    return () => window.removeEventListener("drive-health-changed", onChanged);
  }, []);

  // Iter-194o — drill-down popup dialog. `kind` is one of:
  //   - "files-pending"  → list of POs+invoices > seuil
  //   - "clients-no-drive" → list of clients to migrate Drive folder
  //   - "admin-docs"     → list of clients to migrate admin docs
  // Click on a row dispatches the corresponding *-migration-open event with
  // the client_id detail so the wizard can preselect that exact client.
  const [detailsKind, setDetailsKind] = useState(null);
  const openDetails = (kind) => setDetailsKind(kind);
  const closeDetails = () => setDetailsKind(null);

  const handleClickPending = () => {
    openDetails("files-pending");
  };
  const handleClickClientsWithoutDrive = () => {
    openDetails("clients-no-drive");
  };
  const handleClickAdminDocs = () => {
    openDetails("admin-docs");
  };

  const toggleAutoPurge = async (next) => {
    if (!isCEO) return;
    setTogglingPurge(true);
    try {
      await api.put("/settings", { auto_purge_enabled: next });
      toast.success(next ? "Auto-purge cron resumed" : "Auto-purge cron paused — manual trigger still works");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update auto-purge setting");
    } finally {
      setTogglingPurge(false);
    }
  };

  const runPurge = async () => {
    if (!window.confirm("Run the local-files purge now? Files will only be deleted after Drive presence is verified.")) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/local-files-purge/run");
      const s = r.data || {};
      const purged = (s.purchase_orders?.purged || 0) + (s.invoices?.purged || 0);
      const kept = (s.purchase_orders?.kept || 0) + (s.invoices?.kept || 0);
      toast.success(`Purge done — ${purged} purged, ${kept} kept`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge run failed");
    } finally {
      setRunning(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  // Iter-181 — scan invoices in the default Drive folder
  const checkInvoices = async () => {
    setInvLoading(true);
    try {
      const r = await api.get("/admin/drive-health/invoices-missing");
      setInvCheck(r.data);
      const missing = r.data?.missing_in_drive || 0;
      if (missing === 0) toast.success("All emitted invoices are in Drive");
      else toast.warning(`${missing} invoice(s) missing in the default Drive folder`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to scan invoices");
    } finally {
      setInvLoading(false);
    }
  };

  const uploadMissingInvoices = async () => {
    if (!invCheck) return;
    const eligible = (invCheck.missing || []).filter((m) => m.has_local_pdf);
    if (eligible.length === 0) {
      toast.info("Nothing to upload — no local PDF available for the missing invoices");
      return;
    }
    if (!window.confirm(`Upload ${eligible.length} missing invoice(s) to the default Drive folder?`)) return;
    setInvUploading(true);
    try {
      const r = await api.post("/admin/drive-health/invoices-rattrapage", {
        invoice_ids: eligible.map((e) => e.invoice_id),
      });
      const d = r.data || {};
      toast.success(`Uploaded ${d.uploaded_count || 0} · failed ${d.failed_count || 0}`);
      await checkInvoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setInvUploading(false);
    }
  };

  // Iter-182 — CRA exports coverage
  const checkCraExports = async () => {
    setCraLoading(true);
    try {
      const r = await api.get("/admin/drive-health/cra-exports-missing");
      setCraCheck(r.data);
      const missing = r.data?.missing_in_drive || 0;
      if (missing === 0) toast.success("All sent CRA exports are archived in Drive");
      else toast.warning(`${missing} CRA export(s) missing in Drive (${r.data?.missing_subcontractor || 0} subcontractor)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to scan CRA exports");
    } finally {
      setCraLoading(false);
    }
  };

  const uploadMissingCraExports = async () => {
    if (!craCheck) return;
    const eligible = craCheck.missing || [];
    if (eligible.length === 0) {
      toast.info("Nothing to upload");
      return;
    }
    if (!window.confirm(`Re-upload ${eligible.length} CRA PDF(s) to the per-mission Drive folder?`)) return;
    setCraUploading(true);
    try {
      const r = await api.post("/admin/drive-health/cra-exports-rattrapage", {
        export_ids: eligible.map((e) => e.export_id),
      });
      const d = r.data || {};
      toast.success(`Re-uploaded ${d.uploaded_count || 0} · failed ${d.failed_count || 0}`);
      await checkCraExports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setCraUploading(false);
    }
  };

  const last = health?.last_run || null;
  const driveTokOk = health?.drive_token?.ok;
  const pending = health?.pending_purge?.total || 0;
  const noDrive = health?.clients?.without_drive || 0;
  // Iter-194h — split the noDrive count into "no folder linked" vs "folder
  // linked but subtree not provisioned" so the CEO sees both reasons.
  const noFolder = health?.clients?.without_folder ?? noDrive;
  const incompleteSubtree = health?.clients?.incomplete_subtree || 0;
  // Iter-194n — admin docs migration backlog (clients missing the dedicated
  // `005-Documents Administratifs` subfolder OR docs sitting elsewhere).
  const missingAdminSub = health?.clients?.missing_admin_subfolder || 0;
  const adminDocsToMigrate = health?.clients?.admin_docs_to_migrate || 0;
  const clientsAdminMigrate = health?.clients?.clients_with_admin_docs_to_migrate || 0;
  const adminBacklog = missingAdminSub + clientsAdminMigrate;

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="drive-health-block">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-zinc-900">
          <Activity size={18} className="text-zinc-700" />
          <h2 className="font-heading font-bold text-lg">Drive health</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="drive-health-refresh"
            className="inline-flex items-center gap-1 px-3 h-8 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50 disabled:opacity-50"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={runPurge}
            disabled={running || !driveTokOk}
            data-testid="drive-health-run-purge"
            className="inline-flex items-center gap-1 px-3 h-8 text-xs bg-zinc-950 text-white rounded-md hover:bg-zinc-800 disabled:opacity-50"
            title={!driveTokOk ? "No CEO Drive token — purge would skip everything" : "Run the local-files purge now"}
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <HardDrive size={12} />}
            Run purge now
          </button>
        </div>
      </div>

      <p className="text-xs text-zinc-500 max-w-3xl leading-relaxed">
        The local-files purge cron runs daily at 03:00 UTC. It only deletes a local PDF after Google Drive
        confirms the file still exists. Anything that cannot be verified (token expired, network error, file moved)
        stays on disk — no data is ever lost without a confirmed Drive copy.
      </p>

      {/* Iter-161 — Auto-purge kill-switch (CEO only) */}
      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 flex items-center justify-between gap-3" data-testid="drive-health-auto-purge-toggle-row">
        <div className="flex items-start gap-2 min-w-0">
          {health?.auto_purge_enabled === false ? (
            <Pause size={14} className="text-amber-700 mt-0.5 shrink-0" />
          ) : (
            <Play size={14} className="text-emerald-700 mt-0.5 shrink-0" />
          )}
          <div className="min-w-0">
            <div className="text-xs font-semibold text-zinc-900">
              Auto-purge cron
              <span className={`ml-2 inline-flex items-center px-1.5 h-5 rounded text-[10px] font-mono uppercase tracking-wider ${health?.auto_purge_enabled === false ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid="drive-health-auto-purge-status">
                {health?.auto_purge_enabled === false ? "Paused" : "Active"}
              </span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {health?.auto_purge_enabled === false
                ? "Daily 03:00 UTC cron is paused — no file will be touched. The Run purge now button below still works (manual override)."
                : "Daily 03:00 UTC cron is running. Pause it during a Drive audit or migration to lock all local files in place."}
            </div>
          </div>
        </div>
        <div className="shrink-0">
          {isCEO ? (
            <Switch
              checked={health?.auto_purge_enabled !== false}
              onCheckedChange={(v) => toggleAutoPurge(!!v)}
              disabled={togglingPurge || loading}
              data-testid="drive-health-auto-purge-toggle"
            />
          ) : (
            <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">CEO only</span>
          )}
        </div>
      </div>

      {loading && !health ? (
        <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
      ) : (
        <>
          {/* KPI cards row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <KpiCard
              testid="drive-health-pending"
              icon={<HardDrive size={14} />}
              label={`Files > ${health?.purge_age_days ?? 15}d pending`}
              value={pending}
              tone={pending === 0 ? "ok" : "warn"}
              hint={
                pending === 0
                  ? "All eligible files already purged."
                  : `${health?.pending_purge?.purchase_orders || 0} POs · ${health?.pending_purge?.invoices || 0} invoices`
              }
              onClick={handleClickPending}
              actionLabel="See recent runs"
            />
            <KpiCard
              testid="drive-health-clients-no-drive"
              icon={<FolderOpen size={14} />}
              label="Clients à régulariser"
              value={noDrive}
              tone={noDrive === 0 ? "ok" : "warn"}
              hint={
                noDrive === 0
                  ? `All ${health?.clients?.total || 0} clients have a Drive folder & subtree.`
                  : `${noFolder} sans Drive · ${incompleteSubtree} arborescence incomplète. Ouvrir le wizard.`
              }
              onClick={handleClickClientsWithoutDrive}
              actionLabel="Open migration wizard"
            />
            <KpiCard
              testid="drive-health-admin-docs"
              icon={<FolderOpen size={14} />}
              label="Documents administratifs"
              value={adminBacklog}
              tone={adminBacklog === 0 ? "ok" : "warn"}
              hint={
                adminBacklog === 0
                  ? "Tous les docs admin sont dans 005-Documents Administratifs."
                  : `${missingAdminSub} sous-dossier(s) à créer · ${adminDocsToMigrate} doc(s) à déplacer.`
              }
              onClick={handleClickAdminDocs}
              actionLabel="Open admin docs wizard"
            />
            <KpiCard
              testid="drive-health-token"
              icon={driveTokOk ? <CheckCircle2 size={14} /> : <ShieldAlert size={14} />}
              label="CEO Drive token"
              value={driveTokOk ? "OK" : "Missing"}
              tone={driveTokOk ? "ok" : "danger"}
              hint={
                driveTokOk
                  ? `${health?.drive_token?.ceo_count_with_token || 0} CEO with valid token.`
                  : "No CEO has granted Drive scope. Re-login with Drive to enable verification."
              }
            />
            <KpiCard
              testid="drive-health-last-run"
              icon={<Activity size={14} />}
              label="Last purge run"
              value={last ? fmtDate(last.ran_at) : "Never"}
              tone={last ? "ok" : "warn"}
              hint={
                last
                  ? `${(last.purchase_orders?.purged || 0) + (last.invoices?.purged || 0)} purged · ${(last.purchase_orders?.rattrapage_failed || 0) + (last.invoices?.rattrapage_failed || 0)} rattrapage failed · ${(last.purchase_orders?.drive_unverified || 0) + (last.invoices?.drive_unverified || 0)} unverified`
                  : "Cron has not run yet — click Run purge now."
              }
            />
          </div>

          {/* Recent runs table */}
          {runs.length > 0 && (
            <div className="border border-zinc-200 rounded-md overflow-hidden" data-testid="drive-health-recent-runs">
              <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
              <table className="w-full text-xs" data-testid="drive-health-runs-table">
                <thead className="bg-zinc-50">
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                    <th className="px-3 py-2">Run</th>
                    <th className="px-3 py-2 text-right">Purged</th>
                    <th className="px-3 py-2 text-right">Verified</th>
                    <th className="px-3 py-2 text-right">Unverified</th>
                    <th className="px-3 py-2 text-right">Rattrapage failed</th>
                    <th className="px-3 py-2 text-right">Skipped recent</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r, i) => {
                    const purged = (r.purchase_orders?.purged || 0) + (r.invoices?.purged || 0);
                    const verified = (r.purchase_orders?.drive_verified || 0) + (r.invoices?.drive_verified || 0);
                    const unverified = (r.purchase_orders?.drive_unverified || 0) + (r.invoices?.drive_unverified || 0);
                    const rfail = (r.purchase_orders?.rattrapage_failed || 0) + (r.invoices?.rattrapage_failed || 0);
                    const recent = (r.purchase_orders?.skipped_recent || 0) + (r.invoices?.skipped_recent || 0);
                    return (
                      <tr key={i} className="border-t border-zinc-100" data-testid={`drive-health-run-row-${i}`}>
                        <td className="px-3 py-2 font-mono text-zinc-700">{fmtDate(r.ran_at)}</td>
                        <td className="px-3 py-2 text-right font-mono">{purged}</td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700">{verified}</td>
                        <td className={`px-3 py-2 text-right font-mono ${unverified > 0 ? "text-amber-700" : ""}`}>{unverified}</td>
                        <td className={`px-3 py-2 text-right font-mono ${rfail > 0 ? "text-red-700" : ""}`}>{rfail}</td>
                        <td className="px-3 py-2 text-right font-mono text-zinc-500">{recent}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {!driveTokOk && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900" data-testid="drive-health-no-token-warning">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                <strong>No CEO has a valid Drive token.</strong> The purge cron cannot verify Drive presence, so
                nothing will be deleted from disk. Ask a CEO to log in once with the Drive scope to enable
                verification.
              </div>
            </div>
          )}

          {/* Iter-181 — Client invoices coverage in the default Drive folder */}
          <div className="border-t border-zinc-200 pt-5" data-testid="drive-health-invoices-coverage">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-base flex items-center gap-2">
                  <FileWarning size={15} /> Client invoices · default Drive folder
                </h3>
                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                  Scans every emitted (non-cancelled) client invoice and verifies its copy still lives in the
                  default Drive folder configured in Settings. Missing PDFs can be uploaded back from the local
                  store in one click.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={checkInvoices}
                  disabled={invLoading}
                  data-testid="drive-health-invoices-scan"
                  className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-zinc-300 text-sm rounded-md hover:bg-zinc-50 disabled:opacity-60"
                >
                  {invLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  Scan now
                </button>
              </div>
            </div>

            {invCheck && (
              <div className="space-y-3" data-testid="drive-health-invoices-result">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <KpiCard
                    testid="drive-health-inv-scanned"
                    icon={<HardDrive size={14} />}
                    label="Invoices scanned"
                    value={invCheck.scanned}
                    tone="ok"
                    hint="All emitted, non-cancelled invoices."
                  />
                  <KpiCard
                    testid="drive-health-inv-verified"
                    icon={<CheckCircle2 size={14} />}
                    label="In Drive"
                    value={invCheck.verified_in_drive}
                    tone="ok"
                    hint="File presence confirmed via Drive API."
                  />
                  <KpiCard
                    testid="drive-health-inv-missing"
                    icon={<FileWarning size={14} />}
                    label="Missing in Drive"
                    value={invCheck.missing_in_drive}
                    tone={invCheck.missing_in_drive === 0 ? "ok" : "warn"}
                    hint={invCheck.missing_in_drive === 0
                      ? "All emitted invoices are in Drive."
                      : "Use 'Upload missing' to push from local store."}
                  />
                  <KpiCard
                    testid="drive-health-inv-no-local"
                    icon={<AlertTriangle size={14} />}
                    label="No local PDF"
                    value={invCheck.missing_no_local}
                    tone={invCheck.missing_no_local === 0 ? "ok" : "danger"}
                    hint={invCheck.missing_no_local === 0
                      ? "Every missing invoice has a local copy."
                      : "Local PDF is gone — re-generate or restore from backup."}
                  />
                </div>

                {invCheck.missing_in_drive > 0 && (
                  <div className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="drive-health-inv-action-bar">
                    <div className="text-xs text-amber-900 leading-relaxed min-w-0">
                      <strong>{invCheck.missing_in_drive}</strong> invoice(s) are missing in the default Drive folder.
                      {invCheck.missing_no_local > 0 && (
                        <span className="text-amber-700"> ({invCheck.missing_no_local} have no local PDF available.)</span>
                      )}
                    </div>
                    <button
                      onClick={uploadMissingInvoices}
                      disabled={invUploading || (invCheck.missing || []).filter((m) => m.has_local_pdf).length === 0}
                      data-testid="drive-health-inv-upload-missing"
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600 disabled:opacity-60"
                    >
                      {invUploading ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />}
                      Upload missing
                    </button>
                  </div>
                )}

                {(invCheck.missing || []).length > 0 && (
                  <div className="border border-zinc-200 rounded-md overflow-hidden">
                    <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                    <table className="w-full text-xs" data-testid="drive-health-inv-missing-table">
                      <thead className="bg-zinc-50">
                        <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                          <th className="px-3 py-2">Invoice</th>
                          <th className="px-3 py-2">Client</th>
                          <th className="px-3 py-2">Period</th>
                          <th className="px-3 py-2">Reason</th>
                          <th className="px-3 py-2 text-right">Local PDF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invCheck.missing.slice(0, 30).map((row) => (
                          <tr key={row.invoice_id} className="border-t border-zinc-100" data-testid={`drive-health-inv-row-${row.invoice_id}`}>
                            <td className="px-3 py-2 font-mono text-zinc-800">{row.number}</td>
                            <td className="px-3 py-2 text-zinc-700">{row.client_name || "—"}</td>
                            <td className="px-3 py-2 font-mono text-zinc-500">{row.year}-{String(row.month).padStart(2, "0")}</td>
                            <td className="px-3 py-2 text-zinc-500">
                              {row.reason === "no_drive_file_id" ? "Never uploaded" : "Disappeared from Drive"}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {row.has_local_pdf ? (
                                <span className="text-emerald-700 font-mono text-[11px]">available</span>
                              ) : (
                                <span className="text-red-700 font-mono text-[11px]">missing</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    {invCheck.missing.length > 30 && (
                      <div className="px-3 py-2 text-[11px] text-zinc-500 bg-zinc-50 border-t border-zinc-100">
                        + {invCheck.missing.length - 30} more not shown — they will still be uploaded by the Upload action.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!invCheck && !invLoading && (
              <div className="rounded-md border border-dashed border-zinc-200 p-4 text-xs text-zinc-500" data-testid="drive-health-inv-empty">
                Click <strong>Scan now</strong> to verify every emitted invoice against the default Drive folder.
              </div>
            )}
          </div>

          {/* Iter-182 — Monthly CRA exports coverage (subcontractor + final clients) */}
          <div className="border-t border-zinc-200 pt-5" data-testid="drive-health-cra-coverage">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-base flex items-center gap-2">
                  <FileWarning size={15} /> Monthly CRA exports · per-mission Drive folder
                </h3>
                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                  Each sent CRA email is supposed to archive its PDF in the mission's <code>003-CRA</code>
                  Drive subfolder. Verifies every <code>cra_exports</code> record — especially for subcontractor
                  clients where a silent Drive failure leaves the email out but the archive missing.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={checkCraExports}
                  disabled={craLoading}
                  data-testid="drive-health-cra-scan"
                  className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-zinc-300 text-sm rounded-md hover:bg-zinc-50 disabled:opacity-60"
                >
                  {craLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  Scan now
                </button>
              </div>
            </div>

            {craCheck && (
              <div className="space-y-3" data-testid="drive-health-cra-result">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <KpiCard
                    testid="drive-health-cra-scanned"
                    icon={<HardDrive size={14} />}
                    label="CRA exports scanned"
                    value={craCheck.scanned}
                    tone="ok"
                    hint="All cra_exports rows."
                  />
                  <KpiCard
                    testid="drive-health-cra-verified"
                    icon={<CheckCircle2 size={14} />}
                    label="In Drive"
                    value={craCheck.verified_in_drive}
                    tone="ok"
                    hint="Archive file present in Drive."
                  />
                  <KpiCard
                    testid="drive-health-cra-missing"
                    icon={<FileWarning size={14} />}
                    label="Missing in Drive"
                    value={craCheck.missing_in_drive}
                    tone={craCheck.missing_in_drive === 0 ? "ok" : "warn"}
                    hint={craCheck.missing_in_drive === 0
                      ? "All sent CRAs are archived."
                      : "Click 'Re-upload' to rebuild PDFs and push them now."}
                  />
                  <KpiCard
                    testid="drive-health-cra-missing-sub"
                    icon={<AlertTriangle size={14} />}
                    label="Missing · subcontractor"
                    value={craCheck.missing_subcontractor}
                    tone={craCheck.missing_subcontractor === 0 ? "ok" : "danger"}
                    hint={craCheck.missing_subcontractor === 0
                      ? "No subcontractor CRA missing."
                      : "Subcontractor CRAs are billing-critical — fix first."}
                  />
                </div>

                {craCheck.missing_in_drive > 0 && (
                  <div className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="drive-health-cra-action-bar">
                    <div className="text-xs text-amber-900 leading-relaxed min-w-0">
                      <strong>{craCheck.missing_in_drive}</strong> CRA export(s) need a Drive rattrapage.
                      The PDF is regenerated on-the-fly from CRA data — no local cache required.
                    </div>
                    <button
                      onClick={uploadMissingCraExports}
                      disabled={craUploading}
                      data-testid="drive-health-cra-upload-missing"
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600 disabled:opacity-60"
                    >
                      {craUploading ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />}
                      Re-upload missing
                    </button>
                  </div>
                )}

                {(craCheck.missing || []).length > 0 && (
                  <div className="border border-zinc-200 rounded-md overflow-hidden">
                    <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                    <table className="w-full text-xs" data-testid="drive-health-cra-missing-table">
                      <thead className="bg-zinc-50">
                        <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                          <th className="px-3 py-2">Mission · period</th>
                          <th className="px-3 py-2">Client</th>
                          <th className="px-3 py-2">Type</th>
                          <th className="px-3 py-2">Reason</th>
                          <th className="px-3 py-2">Sent at</th>
                        </tr>
                      </thead>
                      <tbody>
                        {craCheck.missing.slice(0, 30).map((row) => (
                          <tr key={row.export_id} className="border-t border-zinc-100" data-testid={`drive-health-cra-row-${row.export_id}`}>
                            <td className="px-3 py-2 text-zinc-800">
                              <div className="truncate max-w-[280px]">{row.mission_title || "—"}</div>
                              <div className="font-mono text-[10px] text-zinc-500">{row.year}-{String(row.month).padStart(2, "0")}</div>
                            </td>
                            <td className="px-3 py-2 text-zinc-700">{row.client_name || "—"}</td>
                            <td className="px-3 py-2">
                              {row.is_subcontractor ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-800">Sub-traitant</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">Final</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-zinc-500">
                              <div>
                                {row.reason === "no_drive_archive" ? "Drive upload never succeeded" :
                                 row.reason === "no_file_id" ? "Drive archive empty" :
                                 "Disappeared from Drive"}
                              </div>
                              {row.drive_archive_error && (
                                <div className="text-[10px] font-mono text-red-700 mt-0.5 max-w-[260px] truncate" title={row.drive_archive_error}>
                                  {row.drive_archive_error}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono text-zinc-500">{fmtDate(row.sent_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    {craCheck.missing.length > 30 && (
                      <div className="px-3 py-2 text-[11px] text-zinc-500 bg-zinc-50 border-t border-zinc-100">
                        + {craCheck.missing.length - 30} more not shown — they will still be re-uploaded by the action above.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!craCheck && !craLoading && (
              <div className="rounded-md border border-dashed border-zinc-200 p-4 text-xs text-zinc-500" data-testid="drive-health-cra-empty">
                Click <strong>Scan now</strong> to verify every sent CRA export is archived in its mission's Drive folder.
              </div>
            )}
          </div>
        </>
      )}
      {/* Iter-194o — drill-down popup for warn/danger KPI cards */}
      <DriveHealthDetailsDialog
        open={!!detailsKind}
        kind={detailsKind}
        onClose={closeDetails}
      />
    </div>
  );
}

function KpiCard({ testid, icon, label, value, tone = "ok", hint, onClick, actionLabel }) {
  const toneClasses = {
    ok: "border-zinc-200 bg-white",
    warn: "border-amber-200 bg-amber-50",
    danger: "border-red-200 bg-red-50",
  }[tone] || "border-zinc-200 bg-white";
  const valueColor = {
    ok: "text-zinc-900",
    warn: "text-amber-900",
    danger: "text-red-900",
  }[tone] || "text-zinc-900";
  // Iter-193 — when an onClick is provided AND the tone is non-ok (i.e. an alert is
  // raised), make the card behave as a button so the CEO can jump straight to the
  // remediation action without scrolling. ok-tone cards stay non-actionable.
  const isActionable = typeof onClick === "function" && tone !== "ok";
  const Comp = isActionable ? "button" : "div";
  const extraProps = isActionable
    ? {
        type: "button",
        onClick,
        className: `rounded-md border p-3 space-y-1.5 text-left w-full transition-all hover:shadow-sm hover:-translate-y-px focus:outline-none focus:ring-2 focus:ring-zinc-900 cursor-pointer ${toneClasses}`,
      }
    : { className: `rounded-md border p-3 space-y-1.5 ${toneClasses}` };
  return (
    <Comp data-testid={testid} {...extraProps}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
        {icon} {label}
      </div>
      <div className={`text-2xl font-heading font-bold ${valueColor}`}>{value}</div>
      {hint && <div className="text-[11px] text-zinc-600 leading-snug">{hint}</div>}
      {isActionable && actionLabel && (
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-900 mt-1 flex items-center gap-1">
          {actionLabel} →
        </div>
      )}
    </Comp>
  );
}
